import { motion } from "framer-motion";
import { Sparkles, ThumbsUp, ThumbsDown, ArrowRight } from "lucide-react";
import { getTemplateIcon } from "@/lib/templateIcons";
import { getLengthTagInfo } from "@/lib/contentHelpers";
import { DEFAULT_SECTION_TYPES } from "@/config/sectionDefaults";
import { Badge } from "@/components/ui/badge";
import { ContentTag } from "@/components/ui/content-tag";
import { Button } from "@/components/ui/button";
import { useOnboarding } from "@/context/OnboardingContext";
import useUserInteractionHandling from "@/hooks/useUserInteractionHandling";
import useContentGenerationAndWorkflow from "@/hooks/useContentGenerationAndWorkflow";

export default function ContentIdeaSelectionStep() {
  const { likedIdeas } = useOnboarding();
  const { handleThumbsUp, handleThumbsDown } = useUserInteractionHandling();
  const { handleGenerateNewIdeas, handleContinueToContentCreation } =
    useContentGenerationAndWorkflow();

  return (
    <motion.div
      key="step-2"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{
        duration: 0.5,
        ease: "easeInOut",
        opacity: { duration: 0.3 },
      }}
      className="w-full py-4"
    >
      <div className="w-full mx-auto max-w-[43.75rem]">
        <motion.div className="text-center space-y-3 mb-8">
          <motion.h2
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-2xl font-semibold"
          >
            🌱✨ Choose the best ideas 🪴
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="text-muted-foreground"
          >
            ✨ Thumbs up your favourite topics so we can generate content based on your
            website's tone and style.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 mb-8">
          {/* Show visible ideas */}
          {JSON.parse(localStorage.getItem("post_ideas") || "[]")
            .filter((idea: any) => !idea.hidden)
            .map((idea: any, index: number) => (
              <div
                key={`${idea.id}-${index}`}
                className="p-4 rounded-lg border transition-all hover:shadow-subtle bg-card border-border/50"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-2">
                      {/* Template type with icon */}
                      {idea.template_type && (
                        <span className="flex items-center text-sm text-muted-foreground">
                          {getTemplateIcon(idea.template_type)}
                          <span className="mr-2">
                            {idea.template_type.charAt(0).toUpperCase() +
                              idea.template_type.slice(1).replace(/_/g, " ")}
                          </span>
                        </span>
                      )}
                      {/* Length tag with badge */}
                      {idea.length_tag && (
                        <ContentTag
                          type="length"
                          label={idea.length_tag}
                          description={getLengthTagInfo(idea.length_tag).description}
                        />
                      )}
                    </div>
                    <h3 className="font-medium text-base text-balance mb-1">
                      {idea.title}
                    </h3>
                    {/* Description */}
                    {idea.description &&
                      !/^Generated content idea #\d+ for your website\.?$/i.test(
                        idea.description
                      ) && (
                        <div className="text-sm text-muted-foreground mb-1">
                          {/* Logging for debugging */}
                          {console.log("Showing idea description:", idea.description)}
                          {idea.description}
                        </div>
                      )}
                    {/* Post digest (was description) */}
                    {idea.post_digest &&
                      !/^Generated content idea #\d+ for your website\.?$/i.test(
                        idea.post_digest
                      ) && (
                        <div className="text-sm text-muted-foreground mb-1">
                          {/* Logging for debugging */}
                          {console.log("Showing idea post_digest:", idea.post_digest)}
                          {idea.post_digest}
                        </div>
                      )}
                    {/* Section layout display */}
                    {idea.section_layout &&
                      Array.isArray(idea.section_layout) &&
                      idea.section_layout.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1 mb-2">
                          {idea.section_layout
                            .filter(
                              (sectionId: string) =>
                                ![
                                  "callout",
                                  "cta",
                                  "faq",
                                  "feature_list",
                                  "image",
                                  "intro",
                                  "quote",
                                  "steps",
                                  "testimonial",
                                  "text",
                                ].includes(sectionId)
                            )
                            .map((sectionId: string, index: number) => {
                              const section = DEFAULT_SECTION_TYPES.find(
                                (s) => s.id === sectionId
                              );
                              return (
                                <span
                                  key={`${sectionId}-${index}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border text-xs"
                                >
                                  {section ? section.name : sectionId}
                                </span>
                              );
                            })}
                        </div>
                      )}
                    {/* Tags/keywords */}
                    {idea.keywords && idea.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {idea.keywords.map((tag: string, i: number) => (
                          <ContentTag
                            key={i}
                            type="keyword"
                            label={tag}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleThumbsUp(idea.id)}
                      title="Like this idea"
                    >
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </motion.div>
                      <span className="sr-only">Like</span>
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-gray-400 hover:text-gray-500 hover:bg-gray-50"
                      onClick={() => handleThumbsDown(idea.id)}
                      title="Dislike this idea"
                    >
                      <ThumbsDown className="h-3.5 w-3.5" />
                      <span className="sr-only">Dislike</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}

          {/* Show message when all ideas are rated */}
          {JSON.parse(localStorage.getItem("post_ideas") || "[]").filter(
            (idea: any) => !idea.hidden
          ).length === 0 && (
            <div className="text-center py-12 border rounded-lg bg-card">
              <h3 className="text-xl font-medium mb-3">You've rated all the ideas!</h3>
              <p className="text-muted-foreground mb-6">
                {likedIdeas.length > 0
                  ? "Great! We'll continue with your selected ideas."
                  : "Let's generate some fresh content ideas for you."}
              </p>
              {likedIdeas.length > 0 ? (
                <Button
                  onClick={handleContinueToContentCreation}
                  variant="default"
                  className="px-6"
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Continue
                </Button>
              ) : (
                <Button
                  onClick={handleGenerateNewIdeas}
                  variant="default"
                  className="px-6"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate New Ideas
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Show Continue button when at least one idea is liked */}
        {likedIdeas.length > 0 && (
          <div className="flex justify-end items-center border-t pt-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">
                {likedIdeas.length} idea{likedIdeas.length !== 1 ? "s" : ""} selected
              </span>
              <Button onClick={handleContinueToContentCreation} className="gap-2">
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
